import React from 'react';

import {getAddOnLink} from 'shells/billing/add-ons/helpers';
import {ErrorDialog} from 'shells/dialog/error-dialog';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {LicenseType} from 'nutshell-graphql-types';
import type {
    GetUserLicenses_user_licenses as UserLicenseInfo,
    SetLicensesForUserInput,
    SetLicensesForUser_setLicensesForUser_licenseUpdateResponse,
} from 'nutshell-graphql-types';

import {useHandleLicenseToggle} from '../../hooks/use-handle-license-toggle';

// @ts-expect-error
import {useGetUserLicenses, useGetLicenseOverview, useSetLicensesForUser} from './graphql/hooks';
import {LicenseSwitch} from './license-switch';

type Props = {
    userId: string;
};

export function Licensing(props: Props) {
    const [isCrmOn, setIsCrmOn] = React.useState<boolean>(false);
    const [isMarketingOn, setIsMarketingOn] = React.useState<boolean>(false);
    const [isEngagementOn, setIsEngagementOn] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [isErrorDialogOpen, setIsErrorDialogOpen] = React.useState<boolean>(false);

    const licensingRef = React.useRef(null);

    const {licenses, isLoading: isLicensesLoading} = useGetUserLicenses(props.userId);
    const {
        licenseOverview,
        isLoading: isLicenseOverviewLoading,
        refetch: refetchLicenseOverview,
    } = useGetLicenseOverview();
    const {setLicensesForUser} = useSetLicensesForUser();
    const {handleLicenseChange} = useHandleLicenseToggle();

    React.useEffect(() => {
        // @ts-expect-error
        licenses.forEach((licenseData) => {
            switch (licenseData.licenseType) {
                case 'CRM':
                    setIsCrmOn(licenseData.isOwned);
                    break;
                case 'MARKETING':
                    setIsMarketingOn(licenseData.isOwned);
                    break;
                case 'ENGAGEMENT':
                    setIsEngagementOn(licenseData.isOwned);
                    break;
                default:
                    break;
            }
        });
    }, [licenses]);

    const handleSwitchToggle = (licenseType: LicenseType) => {
        let updatedState = false;

        switch (licenseType) {
            case 'CRM':
                updatedState = !isCrmOn;
                setIsCrmOn(updatedState);
                break;
            case 'MARKETING':
                updatedState = !isMarketingOn;
                setIsMarketingOn(updatedState);
                break;
            case 'ENGAGEMENT':
                updatedState = !isEngagementOn;
                setIsEngagementOn(updatedState);
                break;
            default:
                return;
        }

        handleSetLicenses(
            {
                id: props.userId,
                licenses: [
                    {
                        licenseType,
                        isEnabled: updatedState,
                    },
                ],
            },
            licenseType
        );
    };

    const handleSetLicenses = (input: SetLicensesForUserInput, license: LicenseType) => {
        // @ts-expect-error
        setLicensesForUser(input).then((response) => {
            const {
                wasSuccessful,
                errorMessage: responseErrorMessage,
                newLicenseState,
            }: SetLicensesForUser_setLicensesForUser_licenseUpdateResponse = response.data
                .setLicensesForUser.licenseUpdateResponse;

            // Handle response
            if (wasSuccessful) {
                refetchLicenseOverview();
                handleLicenseChange({
                    licenseState: {
                        isEngagementOn: isEngagementOn,
                    },
                    licenseType: license,
                    userId: props.userId,
                });
            } else {
                setIsErrorDialogOpen(true);
                setErrorMessage(responseErrorMessage || '');
            }

            setIsCrmOn(false);
            setIsMarketingOn(false);
            setIsEngagementOn(false);

            // Update the state to match the backend state
            newLicenseState.forEach((licenseType: LicenseType) => {
                switch (licenseType) {
                    case 'CRM':
                        setIsCrmOn(true);
                        break;
                    case 'MARKETING':
                        setIsMarketingOn(true);
                        break;
                    case 'ENGAGEMENT':
                        setIsEngagementOn(true);
                        break;
                }
            });
        });
    };

    const getIsOnStateForLicenseType = (licenseType: LicenseType) => {
        switch (licenseType) {
            case 'CRM':
                return isCrmOn;
            case 'MARKETING':
                return isMarketingOn;
            case 'ENGAGEMENT':
                return isEngagementOn;
            default:
                return false;
        }
    };

    return (
        <div ref={licensingRef}>
            {isLicensesLoading || isLicenseOverviewLoading ? undefined : (
                <>
                    <StackLegacy spacing={8}>
                        {licenses.map((license: UserLicenseInfo) => {
                            const licenseType: LicenseType = license.licenseType;
                            const licenseInfo = licenseOverview.instanceLicenses.find(
                                // @ts-expect-error
                                (l) => l.licenseType === licenseType
                            );

                            return (
                                <LicenseSwitch
                                    key={licenseType}
                                    licenseType={licenseType}
                                    licenseInfo={licenseInfo}
                                    // @ts-expect-error
                                    userLicenseInfo={license}
                                    handleSwitchToggle={handleSwitchToggle}
                                    purchaseLink={getLicensePurchaseLink(licenseType)}
                                    infoLink={getLicenseInfoLink(licenseType)}
                                    isOn={getIsOnStateForLicenseType(licenseType)}
                                />
                            );
                        })}
                    </StackLegacy>
                    {isErrorDialogOpen ? (
                        <ErrorDialog
                            anchor={licensingRef.current}
                            message={errorMessage}
                            onClose={() => {
                                setIsErrorDialogOpen(false);
                                setErrorMessage('');
                            }}
                            location={'left'}
                        />
                    ) : null}
                </>
            )}
        </div>
    );
}

function getLicensePurchaseLink(licenseType: LicenseType): string | null {
    switch (licenseType) {
        case 'CRM':
            return null;
        case 'MARKETING':
            return null;
        case 'ENGAGEMENT':
            return getAddOnLink('INBOX');
    }

    return '';
}

function getLicenseInfoLink(licenseType: LicenseType): string | null {
    switch (licenseType) {
        case 'CRM':
            return null;
        case 'MARKETING':
            return null;
        case 'ENGAGEMENT':
            return 'https://support.nutshell.com/en/articles/9674420-nutshell-sms-how-to-send-and-manage-texts';
    }

    return '';
}
